// PAPERLIKE CUSTOM — Paperlike-built, no vanilla equivalent

if (!window.customElements.get('blog-recent-articles')) {
  // Mirror Shopify's `handleize` filter so client-side tag comparisons match
  // the server-rendered URLs and `data-tag` attrs. Lowercase, collapse any run
  // of non-alphanumeric chars to a single `-`, strip leading/trailing dashes.
  function handleize(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  class BlogRecentArticles extends HTMLElement {
    connectedCallback() {
      this.perPage        = parseInt(this.dataset.perPage, 10) || 9;
      this.blogPath       = this.dataset.blogPath;           // e.g. /blogs/news or /blogs/news/tagged/foo
      this.baseBlogPath   = this.dataset.baseBlogPath;       // always /blogs/news
      this.totalArticles  = parseInt(this.dataset.totalArticles, 10) || 0;

      this.allCards        = Array.from(this.querySelectorAll('[data-article-card]'));
      this.tabs            = this.querySelectorAll('[data-tab-id]');
      this.loadMoreWrapper = this.querySelector('.blog-recent-articles__load-more');
      this.loadMoreBtn     = this.loadMoreWrapper?.querySelector('.button, button');
      this.activeTag       = null;
      this._fetching       = false;
      this._fetchPage      = 1; // last blog page fetched
      this._fetchExhausted = false; // true once fetch returns an empty page
      this._waitingToShow  = false; // user clicked while prefetch was still in flight

      // Handles already in DOM — used to deduplicate AJAX responses
      this._loadedHandles = new Set(
        this.allCards.map(c => c.dataset.articleHandle).filter(Boolean)
      );

      // Initialise shown count from Liquid-rendered visible cards
      this._shownCount = this.allCards.filter(c => !c.hidden).length;

      this.tabs.forEach(tab => tab.addEventListener('click', () => this.activateTab(tab)));
      this.loadMoreBtn?.addEventListener('click', () => this.loadMore());

      // If the page was loaded with a URL tag (e.g. /blogs/news/tagged/cat--drawing),
      // auto-activate the matching tab and re-run visibility so _shownCount stays in sync
      // with the filtered card set. Runs before first paint so there is no visible flash.
      const urlTag = this.dataset.currentTag;
      if (urlTag) {
        const match = Array.from(this.tabs).find(t => t.dataset.tag === urlTag);
        if (match) {
          this.tabs.forEach(t => {
            const active = t === match;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', String(active));
          });
          this.activeTag = match.dataset.tag;
          this.resetVisibility();
        }
      }

      // Kick off background prefetch immediately so the next batch is ready before the user needs it
      this._prefetchIfNeeded();
    }

    activateTab(tab) {
      this.tabs.forEach(t => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active);
      });
      this.activeTag = tab.dataset.tag || null;
      this.resetVisibility();
    }

    get filteredCards() {
      if (!this.activeTag) return this.allCards;
      return this.allCards.filter(card =>
        card.dataset.tags.split(',').map(t => handleize(t)).includes(this.activeTag)
      );
    }

    resetVisibility() {
      this.allCards.forEach(card => { card.hidden = true; });
      const visible = this.filteredCards;
      visible.slice(0, this.perPage).forEach(card => { card.hidden = false; });
      this._shownCount = Math.min(this.perPage, visible.length);
      this._updateLoadMore();
    }

    loadMore() {
      const visible = this.filteredCards;
      const next    = visible.slice(this._shownCount, this._shownCount + this.perPage);

      if (next.length > 0) {
        // Cards already in DOM — show immediately
        next.forEach(card => { card.hidden = false; });
        this._shownCount += next.length;
        this._updateLoadMore();

        // Start next prefetch when we're getting low on hidden cards
        const hiddenRemaining = visible.length - this._shownCount;
        if (hiddenRemaining < this.perPage) {
          this._prefetchIfNeeded();
        }
      } else if (this._fetching) {
        // Prefetch is still in flight — hold the click, re-fire once data arrives
        this._waitingToShow = true;
        if (this.loadMoreBtn) this.loadMoreBtn.setAttribute('disabled', '');
      } else if (this._fetchExhausted || this.allCards.length >= this.totalArticles) {
        if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
      } else {
        // No matching cards in DOM yet, but more server pages exist — fetch and wait
        this._prefetchIfNeeded();
        if (this._fetching) {
          this._waitingToShow = true;
          if (this.loadMoreBtn) this.loadMoreBtn.setAttribute('disabled', '');
        } else {
          if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
        }
      }
    }

    _prefetchIfNeeded() {
      if (this._fetching || this._fetchExhausted || !this.blogPath) return;
      if (this.allCards.length < this.totalArticles || this.dataset.urlFiltered) {
        this._fetchNextPage();
      }
    }

    _updateLoadMore() {
      if (!this.loadMoreWrapper) return;
      const allShown       = this._shownCount >= this.filteredCards.length;
      const noMoreOnServer = this._fetchExhausted || this.allCards.length >= this.totalArticles;
      this.loadMoreWrapper.hidden = allShown && noMoreOnServer;
    }

    async _fetchNextPage() {
      if (this._fetching || !this.blogPath) return;
      this._fetching = true;

      const nextPage = this._fetchPage + 1;
      // Fetch via the ?view=fetch alternative template (blog.fetch.json + blog-articles-feed.liquid).
      // This uses {% paginate %} so it serves all articles beyond the 50-article section limit,
      // without requiring the main-blog section to be visible on the blog page.
      const url = `${this.blogPath}?view=fetch&page=${nextPage}`;

      try {
        const response = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        const blogGrid = doc.querySelector('.blog-posts');
        if (!blogGrid) throw new Error('.blog-posts not found in response');

        const fetched = Array.from(blogGrid.querySelectorAll('blog-post-card[data-article-handle]'));

        // Deduplicate: skip articles already rendered in our section
        const fresh = fetched.filter(card => {
          const handle = card.dataset.articleHandle;
          return handle && !this._loadedHandles.has(handle);
        });

        if (fresh.length > 0) {
          const grid = this.querySelector('.blog-recent-articles__grid');
          fresh.forEach(card => {
            const handle = card.dataset.articleHandle;
            const tags   = card.dataset.tags || '';
            const wrapper = document.createElement('div');
            wrapper.dataset.articleCard   = '';
            wrapper.dataset.articleHandle = handle;
            wrapper.dataset.tags          = tags;
            wrapper.hidden                = true;
            wrapper.appendChild(card.cloneNode(true));
            grid.appendChild(wrapper);
            this._loadedHandles.add(handle);
          });

          this.allCards   = Array.from(this.querySelectorAll('[data-article-card]'));
          this._fetchPage = nextPage;
          this._fetching  = false;

          if (this._waitingToShow) {
            // User clicked while we were fetching — show the cards now
            this._waitingToShow = false;
            if (this.loadMoreBtn) this.loadMoreBtn.removeAttribute('disabled');
            this.loadMore();
          } else {
            this._updateLoadMore();
          }
        } else {
          this._fetchPage = nextPage;
          this._fetching  = false;

          if (fetched.length === 0) {
            // Empty page — we've reached the true end (handles tagged URLs where totalArticles is the whole-blog count)
            this._fetchExhausted = true;
            this._waitingToShow  = false;
            if (this.loadMoreBtn) this.loadMoreBtn.removeAttribute('disabled');
            this._updateLoadMore();
          } else if (this.allCards.length < this.totalArticles) {
            // Non-empty page but all articles already in DOM — skip to next page
            this._fetchNextPage();
          } else {
            this._waitingToShow = false;
            if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
          }
        }
      } catch (e) {
        console.warn('blog-recent-articles: fetch failed', e);
        this._fetching      = false;
        this._waitingToShow = false;
        if (this.loadMoreBtn) this.loadMoreBtn.removeAttribute('disabled');
        if (this._shownCount >= this.filteredCards.length) {
          if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
        }
      }
    }
  }

  customElements.define('blog-recent-articles', BlogRecentArticles);
}
